import { ref } from 'vue';
import { requestJson } from '@/shared/request/http-client';
import type { PlayerAuthResponse } from '@/entities/session/types';
import { clientEnv } from '@/shared/config/env';
import { tokenStorage } from '@/entities/session/token-storage';
import {
  beginSimulationBind,
  clearSimulationSession,
  getSimulationApiBaseUrl,
  getSimulationToken,
  hasSimulationMarker,
  isCurrentSimulationBind,
  markSimulationPending,
  readSimulationPendingBase,
  setSimulationSession,
} from './simulation-state';

export const isSimulationPlayer = (accountKind?: string | null) => accountKind === 'SIMULATION';

export const isSimulationSession = (accountKind?: string | null) =>
  isSimulationPlayer(accountKind) || hasSimulationMarker();

export const isSimulationAllowedRoute = (path: string) =>
  path === '/' ||
  path === '/home' ||
  path.startsWith('/gameDetails') ||
  path.startsWith('/games') ||
  path.startsWith('/venue') ||
  path === '/account';

export const simulationNoticeOpen = ref(false);

export const notifySimulationUnsupported = () => {
  simulationNoticeOpen.value = true;
};

export class SimulationUnsupportedError extends Error {
  constructor() {
    super('SIMULATION_ACCOUNT_UNSUPPORTED');
    this.name = 'SimulationUnsupportedError';
  }
}

export const closeSimulationNotice = () => {
  simulationNoticeOpen.value = false;
};

/** H5 业务 path 是 `/v1/...`，基址必须带 `/api`，与 VITE_API_BASE_URL=/api 一致。 */
export const resolveSimulationApiBase = (url: string) => {
  const base = url.replace(/\/$/, '');
  return base.endsWith('/api') ? base : `${base}/api`;
};

export const bindSimulationSession = async (
  accountKind: string | undefined,
  simulationApiBaseUrl: string | undefined,
  siteId: string,
  realToken: string,
) => {
  if (!isSimulationPlayer(accountKind) || !simulationApiBaseUrl) {
    clearSimulationSession();
    return;
  }
  const baseUrl = resolveSimulationApiBase(simulationApiBaseUrl);
  const generation = beginSimulationBind();
  const ticket = await requestJson<{ simulationApiBaseUrl: string; ticket: string }>(
    `/v1/app/auth/simulation-ticket?siteId=${encodeURIComponent(siteId)}`,
    { authToken: realToken, method: 'POST' },
  );
  if (!isCurrentSimulationBind(generation)) return;
  const exchanged = await requestJson<PlayerAuthResponse>('/v1/app/auth/simulation-exchange', {
    body: { siteId, ticket: ticket.ticket },
    method: 'POST',
    simulationBaseUrl: baseUrl,
    skipSimulationToken: true,
  });
  if (!isCurrentSimulationBind(generation)) return;
  setSimulationSession(exchanged.accessToken, baseUrl, exchanged.player?.siteId || siteId);
};

let bindInFlight: Promise<void> | null = null;

export const rememberSimulationPending = (accountKind: string | undefined, simulationApiBaseUrl?: string | null) => {
  if (!isSimulationPlayer(accountKind) || !simulationApiBaseUrl) {
    clearSimulationSession();
    return;
  }
  markSimulationPending(resolveSimulationApiBase(simulationApiBaseUrl));
};

/** 登录不换票；余额、充值、进游戏首次访问再切模拟域。 */
export const ensureSimulationBound = async () => {
  if (getSimulationToken() && getSimulationApiBaseUrl()) return;
  const pending = readSimulationPendingBase();
  const realToken = tokenStorage.read();
  if (!pending || !realToken) return;
  if (!bindInFlight) {
    bindInFlight = bindSimulationSession('SIMULATION', pending, clientEnv.siteId, realToken).finally(() => {
      bindInFlight = null;
    });
  }
  await bindInFlight;
};
